import React, { useEffect, useMemo, useRef, useState } from 'react'
import { message } from 'antd'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import SewingHandoverHistoryPage from './SewingHandoverHistoryPage'
import {
    getSewingHandovers, getSewingHandoverHandedBy, getSewingHandoverDetail, listPartner,
} from '../../../../services/api/3dApi'
import { HISTORY_PAGE_SIZE, getDefaultFilters } from '../../helpers/history'
import '../../styles/history.scss'

const FILTER_KEYS = ['from', 'to', 'partner', 'handed_by', 'batch_name']

const EMPTY_SUMMARY = { total_handovers: 0, total_batches: 0, total_quantity: 0 }

// Query string là nguồn sự thật của bộ lọc: F5 / chia sẻ link giữ nguyên kết quả.
const _parseQuery = (search) => {
    const parsed = queryString.parse(search)
    const hasFilter = FILTER_KEYS.some(key => key in parsed)
    const filters = hasFilter ? FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: parsed[key] || '' }), {}) : getDefaultFilters()
    const page = Math.max(parseInt(parsed.page, 10) || 1, 1)

    return { filters, page }
}

const SewingHandoverHistoryContainer = () => {
    const history = useHistory()
    const location = useLocation()
    const { filters, page } = useMemo(() => _parseQuery(location.search), [location.search])

    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [summary, setSummary] = useState(EMPTY_SUMMARY)
    const [partners, setPartners] = useState([])
    const [handedByList, setHandedByList] = useState([])

    const [detailVisible, setDetailVisible] = useState(false)
    const [detailLoading, setDetailLoading] = useState(false)
    const [detail, setDetail] = useState(null)

    // Enter ở ô Tên lô / Xoá lọc khi URL không đổi vẫn phải tải lại danh sách.
    const [reloadKey, setReloadKey] = useState(0)
    // Chỉ nhận response của request mới nhất (đổi trang / lọc nhanh, mở drawer liên tiếp).
    const listRequestRef = useRef(0)
    const detailRequestRef = useRef(0)

    const _fetchPartners = async () => {
        try {
            // Bộ lọc chỉ lấy đối tác đang Hiện (hidden: false, như màn Quản lý đối tác).
            // Danh sách lịch sử gửi kèm partner_visible nên lần giao của đối tác đã ẩn cũng không hiện ở bảng.
            const query = queryString.stringify({ type: 'sewing_partner', hidden: false, limit: 10000, page: 1 })
            const { data: res } = await listPartner(query)
            const { success, data, error } = res || {}
            if (!success) throw new Error(error)
            setPartners((data && data.partners) || [])
        } catch (e) {
            message.error(e.message)
        }
    }

    const _fetchHandedBy = async () => {
        try {
            const { data: res } = await getSewingHandoverHandedBy()
            const { success, data, error } = res || {}
            if (!success) throw new Error(error)
            setHandedByList(Array.isArray(data) ? data : [])
        } catch (e) {
            message.error(e.message)
        }
    }

    const _fetchHandovers = async () => {
        const requestId = ++listRequestRef.current
        setLoading(true)
        try {
            // partner_visible: chỉ lấy lịch sử của đối tác đang Hiện, khớp danh sách ở bộ lọc Đối tác may (3d-api FAC-26884)
            const query = queryString.stringify(
                { ...filters, page, limit: HISTORY_PAGE_SIZE, partner_visible: true },
                { skipEmptyString: true },
            )
            const { data: res } = await getSewingHandovers(query)
            if (requestId !== listRequestRef.current) return
            const { success, data, error } = res || {}
            if (!success) throw new Error(error)
            setItems(data.items || [])
            setTotal(data.total || 0)
            setSummary(data.summary || EMPTY_SUMMARY)
        } catch (e) {
            if (requestId !== listRequestRef.current) return
            setItems([])
            setTotal(0)
            setSummary(EMPTY_SUMMARY)
            message.error(e.message)
        } finally {
            if (requestId === listRequestRef.current) setLoading(false)
        }
    }

    useEffect(() => {
        _fetchPartners()
        _fetchHandedBy()
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        _fetchHandovers()
        // eslint-disable-next-line
    }, [location.search, reloadKey])

    const _pushQuery = (nextFilters, nextPage) => {
        const search = queryString.stringify({ ...nextFilters, page: nextPage > 1 ? nextPage : undefined })
        if (search === location.search.replace(/^\?/, '')) {
            setReloadKey(key => key + 1)
            return
        }
        history.push({ pathname: location.pathname, search })
    }

    const _onSearch = (nextFilters) => _pushQuery(nextFilters, 1)

    const _onReset = () => _pushQuery(getDefaultFilters(), 1)

    const _onChangePage = (nextPage) => _pushQuery(filters, nextPage)

    const _onViewDetail = async (handoverId) => {
        const requestId = ++detailRequestRef.current
        setDetailVisible(true)
        setDetailLoading(true)
        setDetail(null)
        try {
            const { data: res } = await getSewingHandoverDetail(handoverId)
            if (requestId !== detailRequestRef.current) return
            const { success, data, error } = res || {}
            if (!success) throw new Error(error)
            setDetail(data)
        } catch (e) {
            if (requestId !== detailRequestRef.current) return
            message.error(e.message)
        } finally {
            if (requestId === detailRequestRef.current) setDetailLoading(false)
        }
    }

    const _onCloseDetail = () => {
        detailRequestRef.current += 1
        setDetailVisible(false)
        setDetailLoading(false)
    }

    const _onPrint = (handoverId) => history.push(`/sewing-handover/${handoverId}/print`)

    return (
        <div className='SewingHandoverHistoryContainer'>
            <SewingHandoverHistoryPage
                filters={filters}
                partners={partners}
                handedByList={handedByList}
                loading={loading}
                items={items}
                total={total}
                page={page}
                summary={summary}
                detailVisible={detailVisible}
                detailLoading={detailLoading}
                detail={detail}
                onSearch={_onSearch}
                onReset={_onReset}
                onChangePage={_onChangePage}
                onViewDetail={_onViewDetail}
                onCloseDetail={_onCloseDetail}
                onPrint={_onPrint}
            />
        </div>
    )
}

export default SewingHandoverHistoryContainer
